import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { LineChart as LineIcon } from "lucide-react";

const CATS = {
  dinks: { label: "Dinks", color: "#4ADE80" },
  drops: { label: "Drops", color: "#16A34A" },
  drives: { label: "Drives", color: "#A3E635" },
  resets: { label: "Resets", color: "#10B981" },
};

export default function DrillScoreTrends() {
  const [analyses, setAnalyses] = useState([]);

  useEffect(() => {
    api.get("/videos/analyses", { params: { kind: "drill" } })
      .then(({ data }) => setAnalyses(data.filter((a) => a.status === "complete" && a.score != null && a.category).reverse()))
      .catch(() => {});
  }, []);

  if (analyses.length === 0) return null;

  const chartData = analyses.map((a, i) => ({
    date: a.created_at ? new Date(a.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : `#${i + 1}`,
    [a.category]: a.score,
  }));
  const present = [...new Set(analyses.map((a) => a.category))];
  const best = present.length > 1
    ? present.map((c) => {
        const scores = analyses.filter((a) => a.category === c).map((a) => a.score);
        return { c, delta: scores[scores.length - 1] - scores[0] };
      }).sort((a, b) => b.delta - a.delta)[0]
    : null;

  return (
    <div className="bg-pb-surface border border-pb-border p-5" data-testid="drill-score-trends">
      <div className="flex items-center gap-2 mb-1">
        <LineIcon className="w-4 h-4 text-pb-red" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Drill Video Score Trends</span>
      </div>
      {best && best.delta > 0 && (
        <div className="text-xs mb-2" data-testid="fastest-improving">
          Fastest improving: <span className="font-bold" style={{ color: CATS[best.c]?.color }}>{CATS[best.c]?.label}</span>
          <span className="text-pb-cyan font-mono ml-1">+{best.delta}</span>
        </div>
      )}
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={chartData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
          <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" tick={{ fill: "#71717A", fontSize: 10 }} axisLine={{ stroke: "#27272A" }} tickLine={false} />
          <YAxis domain={[0, 100]} tick={{ fill: "#52525B", fontSize: 10 }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid #27272A", color: "#fff" }} labelStyle={{ color: "#A1A1AA" }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {present.map((c) => (
            <Line key={c} type="monotone" dataKey={c} name={CATS[c]?.label || c} stroke={CATS[c]?.color || "#A3E635"}
              strokeWidth={2} connectNulls dot={{ r: 3, fill: CATS[c]?.color }} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
